/**
 * Helpers puros da página da AVR (Cliente → Projeto → Unidade/Área →
 * Equipamento; filtros de fontes; candidatos a signatário). Sem DOM, para
 * serem exercitados em teste. O contexto reaproveita os helpers do ME/LP.
 */

import type { AvrSeed } from "../../application/services/avr-report-service.js";
import { isAvrLocked, isAvrSubmittable, type AvrReportStatus, type AvrSourceKind, type AvrSourceReport } from "../../domain/engineering/avr-report-domain.js";
import { PermissionCode } from "../../permissions/permissions.js";

export { filterEquipmentsByContext, staleMeContextFields as staleAvrContextFields } from "./me-report-context.js";
export { plhCandidatesForClient, formatSignatoryOption } from "./lp-report-context.js";

export interface AvrContextSelection {
  clientId: string;
  projectId: string;
  unitAreaId: string;
  equipmentId: string;
  inspectionId: string;
}

export function missingAvrContextFields(selection: AvrContextSelection): string[] {
  const missing: string[] = [];
  if (!selection.clientId) missing.push("o Cliente");
  if (!selection.projectId) missing.push("o Projeto");
  if (!selection.unitAreaId) missing.push("a Unidade / Área");
  if (!selection.equipmentId) missing.push("o Equipamento / TAG");
  return missing;
}

export function canCreateAvrFromContext(selection: AvrContextSelection): boolean {
  return missingAvrContextFields(selection).length === 0;
}

/** Só identificação vem do cadastro — NENHUMA espessura do Equipment entra na AVR. */
export function buildAvrSeedFromContext(input: {
  selection: AvrContextSelection;
  client?: any;
  project?: any;
  unitArea?: any;
  equipment?: any;
  equipmentTypeLabels?: Record<string, string>;
}): AvrSeed {
  const { selection, client, project, unitArea, equipment } = input;
  const labels = input.equipmentTypeLabels ?? {};
  return {
    clientId: selection.clientId,
    clientDisplayName: client ? (client.name || client.legalName || "") : "",
    projectId: selection.projectId || null,
    projectContract: project ? `${project.code} — ${project.name}` : "",
    unitAreaId: selection.unitAreaId || null,
    unitArea: unitArea ? `${unitArea.code} — ${unitArea.name}` : "",
    equipmentId: selection.equipmentId || null,
    equipmentTag: equipment?.tag || "",
    equipmentTypeLabel: equipment ? (labels[equipment.equipmentType] || equipment.equipmentType || "") : "",
    material: equipment ? (equipment.material || equipment.materialSpecification || equipment.bodyMaterial || "") : "",
    fluid: equipment ? (equipment.fluid || equipment.service || "") : ""
  };
}

export interface AvrSourceFilterState {
  kind: AvrSourceKind | "ALL";
  from: string;
  to: string;
}

/** Filtros TIPO / PERÍODO da tabela de fontes — só ME e B-Scan existem para filtrar. */
export function applyAvrSourceFilters(sources: readonly AvrSourceReport[], filters: AvrSourceFilterState): AvrSourceReport[] {
  return sources
    .filter(s => filters.kind === "ALL" || s.kind === filters.kind)
    .filter(s => !filters.from || s.testDate >= filters.from)
    .filter(s => !filters.to || s.testDate <= filters.to);
}

/** Candidatos a "ELABORADO POR": qualquer Profissional ativo — o já escolhido nunca some. */
export function elaboratorCandidates(professionals: readonly any[], currentId?: string | null): any[] {
  return professionals
    .filter(p => p.status === "ACTIVE" || (currentId && p.id === currentId))
    .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "pt-BR"));
}

/** Classe CSS do badge de status; CÁLCULO DESATUALIZADO tem destaque próprio. */
export function statusBadgeClass(status: AvrReportStatus): string {
  switch (status) {
    case "SIGNED": return "status-signed";
    case "PLH_APPROVED": return "status-approved";
    case "REJECTED": return "status-outdated";
    case "AWAITING_PLH_REVIEW":
    case "PLH_REVIEWING": return "status-review";
    case "CORRECTION_REQUESTED": return "status-correction";
    case "VALIDATED": return "status-validated";
    case "OUTDATED": return "status-outdated";
    case "CALCULATED": return "status-calculated";
    default: return "status-draft";
  }
}

/**
 * Quais ações a elaboração oferece para um status + conjunto de permissões.
 * É a única fonte da regra "o botão aparece?" — a página só renderiza o
 * que sai daqui, sem consultar role em lugar nenhum.
 */
export interface AvrWorkflowActions {
  locked: boolean;
  inCorrection: boolean;
  canEdit: boolean;
  canSources: boolean;
  canCalculate: boolean;
  canValidate: boolean;
  canGeneratePdf: boolean;
  showSubmit: boolean;
  submitLabel: string;
  canSign: boolean;
}

export function avrWorkflowActions(status: AvrReportStatus, has: (permission: string) => boolean): AvrWorkflowActions {
  const locked = isAvrLocked(status);
  const inCorrection = status === "CORRECTION_REQUESTED";
  const editable = !locked;
  return {
    locked,
    inCorrection,
    canEdit: editable && has(PermissionCode.AvrUpdate),
    canSources: editable && has(PermissionCode.AvrSelectSources),
    canCalculate: editable && has(PermissionCode.AvrCalculate),
    canValidate: editable && has(PermissionCode.AvrValidate),
    canGeneratePdf: has(PermissionCode.AvrGeneratePdf),
    showSubmit: isAvrSubmittable(status) && has(PermissionCode.AvrSubmitForPlhReview),
    submitLabel: inCorrection ? "Enviar Novamente para Análise do PLH" : "Enviar para Análise do PLH",
    canSign: status === "PLH_APPROVED" && has(PermissionCode.AvrApprove) && has(PermissionCode.AvrSign)
  };
}

/** Ações do Workspace de análise do PLH: só quem tem a permissão E é o PLH designado. */
export interface AvrPlhReviewActions {
  isAssigned: boolean;
  canStart: boolean;
  canApprove: boolean;
  canRequestCorrection: boolean;
  canReturn: boolean;
  /** REPROVAR AVR — motivo obrigatório; a AVR não é excluída. */
  canReject: boolean;
}

export function avrPlhReviewActions(status: AvrReportStatus, assignedProfessionalId: string | null, currentProfessionalId: string | null, has: (permission: string) => boolean): AvrPlhReviewActions {
  const isAssigned = !!assignedProfessionalId && !!currentProfessionalId && String(assignedProfessionalId) === String(currentProfessionalId);
  const underReview = status === "AWAITING_PLH_REVIEW" || status === "PLH_REVIEWING";
  return {
    isAssigned,
    canStart: isAssigned && status === "AWAITING_PLH_REVIEW" && has(PermissionCode.AvrPlhReview),
    canApprove: isAssigned && underReview && has(PermissionCode.AvrApprovePlhReview),
    canRequestCorrection: isAssigned && (underReview || status === "PLH_APPROVED") && has(PermissionCode.AvrRequestCorrection),
    canReturn: isAssigned && underReview && has(PermissionCode.AvrRequestCorrection),
    canReject: isAssigned && (underReview || status === "PLH_APPROVED") && has(PermissionCode.AvrRequestCorrection)
  };
}

/** Fila "AVR — ANÁLISE DO PLH": o que está aguardando ou em análise. */
export const AVR_PLH_QUEUE_STATUSES: readonly AvrReportStatus[] = ["AWAITING_PLH_REVIEW", "PLH_REVIEWING"];

export function avrEngineeringCounts(reports: readonly { status: AvrReportStatus }[]): { elaboracao: number; aguardandoPlh: number; correcao: number; aprovada: number; assinada: number; pendentesPlh: number } {
  const count = (pred: (s: AvrReportStatus) => boolean) => reports.filter(r => pred(r.status)).length;
  return {
    elaboracao: count(s => s === "DRAFT" || s === "CALCULATED" || s === "OUTDATED" || s === "VALIDATED"),
    aguardandoPlh: count(s => s === "AWAITING_PLH_REVIEW" || s === "PLH_REVIEWING"),
    correcao: count(s => s === "CORRECTION_REQUESTED"),
    aprovada: count(s => s === "PLH_APPROVED"),
    assinada: count(s => s === "SIGNED"),
    pendentesPlh: count(s => AVR_PLH_QUEUE_STATUSES.includes(s))
  };
}
